import produce from 'immer'

import { CALL } from 'store/actions/types'
import { CallDirection } from 'common/Enums'
import Webphone from 'common/Webphone'
import { joinConferenceCallNumbers, removeConferenceCallNumber, splitConferenceCallNumberArray } from 'utils/numberUtils'

const initialState = {
    transferNumber: '',
    enteredPhoneNumber: '',
    destinationNumber: '',
    calls: [],
    isTransferSuccess: false,
    isTransferFail: false,
    isOnTransfer: false,
    validationPhoneError: null
}

export default function callReducer(state = initialState, action) {
    const { type, payload } = action
    switch (type) {
        case CALL.CHANGE_PHONE_NUMBER:
            return produce(state, (draft) => {
                if (payload) {
                    draft.enteredPhoneNumber = payload.number
                }
            })

        case CALL.CHANGE_DESTINATION_NUMBER:
            return produce(state, (draft) => {
                if (payload) {
                    draft.destinationNumber = payload.number
                }
            })

        case CALL.SETUP_CALL:
            return produce(state, (draft) => {
                if (payload.number) {
                    const callIndex = getCallIndex(state, payload.number)
                    const callExists = callIndex > -1
                    const calls = [...state.calls]

                    if (callExists) {
                        const alreadyActiveIndex = calls.findIndex((item) => item.isActive)
                        const isThereAlreadyActive = alreadyActiveIndex > -1

                        if (isThereAlreadyActive) {
                            calls[alreadyActiveIndex] = {
                                ...calls[alreadyActiveIndex],
                                isActive: false
                            }
                        }

                        calls[callIndex] = {
                            ...calls[callIndex],
                            isTrying: false,
                            isCalling: true,
                            isActive: true
                        }
                    } else {
                        const alreadyTryingIndex = calls.findIndex(
                            (item) => item.isTrying && item.direction === CallDirection.Incoming
                        )
                        const anyIsAlreadyTrying = alreadyTryingIndex > -1

                        if (anyIsAlreadyTrying) {
                            calls[alreadyTryingIndex] = {
                                ...calls[alreadyTryingIndex],
                                isTrying: false,
                                isMinimized: true
                            }
                        }

                        calls.push({
                            isTrying: true,
                            isCalling: false,
                            isActive: false,
                            direction: payload.direction || CallDirection.Outgoing,
                            isFinished: false,
                            number: payload.number,
                            displayName: payload.displayName,
                            isTimerActive: false,
                            isHold: false,
                            isMuteMic: false,
                            isClick2Call: payload.isClick2Call
                        })
                    }

                    draft.calls = calls.filter((item) => !item.isFinished)
                }
            })

        case CALL.START_CALL:
            return produce(state, (draft) => {
                const call = state.calls.find((item) => item.number === payload.number)

                // reset to default value incoming call state when accept
                if (call.direction === CallDirection.Incoming) draft.incomingCall = null

                draft.calls = state.calls.map((item) => {
                    const newItem = { ...item }
                    if (newItem.number === payload.number) {
                        newItem.isActive = true
                        newItem.isCalling = false
                        newItem.isHold = false
                        newItem.isTimerActive = true
                    } else {
                        newItem.isActive = false
                        newItem.isHold = true
                        newItem.isTimerActive = false
                    }
                    return newItem
                })
            })

        case CALL.END_CALL:
            return produce(state, (draft) => {
                if (!payload.number) {
                    draft.calls = []
                    return
                }

                draft.calls = state.calls.map((item) => {
                    let draftItem = { ...item }

                    if(item.number === payload.number) {
                        draftItem = {
                            ...item,
                            isFinished: true,
                            isHold: false,
                            isTimerActive: false,
                            isActive: false,
                            isMinimized: false
                        } 
                    } else if (item.isConference && item.numbers.includes(payload.number)) {
                        const isFinished = item.numbers.length === 1
                        const { keepNumber } = payload
                    
                        draftItem = {
                            ...item,
                            isFinished,
                            isTimerActive: !isFinished,
                            isActive: !isFinished,
                            numbers: item.numbers.filter(num => num !== payload.number),
                            number: keepNumber ? item.number : removeConferenceCallNumber(item.number, payload.number)
                        }
                    }

                    return draftItem
                })

                // Set active last active held call if there any
                const heldCalls = draft.calls.filter((call) => call.number !== payload.number && call.isHold)
                const indexOfLastHeld = draft.calls.indexOf(heldCalls[heldCalls.length - 1])

                if (indexOfLastHeld > -1) {
                    const lastHeldCall = draft.calls[indexOfLastHeld]
                    lastHeldCall.isActive = true
                    Webphone.activateCallAudio(lastHeldCall.number)
                }

                const resetTransfer = () => {
                    draft.transferNumber = ''
                    draft.isOnTransfer = state.isOnTransfer ? false : draft.isOnTransfer
                }

                if (state.enteredPhoneNumber === payload.number) {
                    draft.enteredPhoneNumber = ''
                    resetTransfer()
                }

                if (state.transferNumber === payload.number) {
                    resetTransfer()
                }
            })

        case CALL.MERGE_CALLS:
            return produce(state, (draft) => {
                const numbers = splitConferenceCallNumberArray(payload)
                const newCalls = state.calls.filter(call => !payload.includes(call.number))

                newCalls.push({
                    isTrying: false,
                    isCalling: false,
                    isActive: true,
                    isFinished: false,
                    numbers,
                    number: joinConferenceCallNumbers(numbers),
                    displayName: null,
                    isTimerActive: true,
                    isHold: false,
                    isMuteMic: false,
                    isClick2Call: false,
                    isConference: true
                });
                
                draft.calls = newCalls
            })
    
        case CALL.MINIMIZE_CALL:
            return produce(state, (draft) => {
                draft.calls = state.calls.map((item) => {
                    const newItem = { ...item }

                    if (newItem.number === payload.number) {
                        newItem.isTrying = false
                        newItem.isMinimized = true
                    }

                    return newItem
                })
            })

        case CALL.MAXIMIZE_CALL:
            return produce(state, (draft) => {
                const calls = [...state.calls]

                const alreadyTryingIndex = calls.findIndex(
                    (item) => item.isTrying && item.direction === CallDirection.Incoming
                )
                const isThereAlreadyTrying = alreadyTryingIndex > -1

                if (isThereAlreadyTrying) {
                    calls[alreadyTryingIndex] = {
                        ...calls[alreadyTryingIndex],
                        isTrying: false,
                        isMinimized: true
                    }
                }

                draft.calls = calls.map((item) => {
                    const newItem = { ...item }

                    if (newItem.number === payload.number) {
                        newItem.isTrying = true
                        newItem.isMinimized = false
                    }

                    return newItem
                })
            })

        case CALL.REMOVE_CALL:
            return produce(state, (draft) => {
                draft.calls = state.calls.filter(({ number, isFinished, numbers = [], isConference }) => {
                    const differentNumber = !isConference && number !== payload.number
                    const notFinished = !isFinished
                    const hasOtherConferenceNumbers = numbers.length > 0 && !numbers.includes(payload.number)

                    return differentNumber || notFinished || hasOtherConferenceNumbers
                })
            })

        case CALL.REMOVE_CALLS:
            return produce(state, (draft) => {
                draft.calls = []
            })

        case CALL.CHANGE_HOLD:
            return produce(state, (draft) => {
                draft.calls = state.calls.map((item) => {
                    const newItem = { ...item }
                    if (newItem.number === payload.number) {
                        newItem.isHold = payload.value
                        newItem.isTimerActive = !payload.value
                        newItem.isMuteMic = false
                    }
                    return newItem
                })
            })

        case CALL.CHANGE_MUTE_MIC:
            return produce(state, (draft) => {
                draft.calls = state.calls.map((item) => {
                    const newItem = { ...item }
                    if (newItem.number === payload.number) {
                        newItem.isMuteMic = payload.value
                    }
                    return newItem
                })
            })

        case CALL.ADD_CALL:
            const isCallExist = state.calls.findIndex((item) => item.number === payload.number) > -1
            if (!isCallExist) {
                const deactivatedNumbers = state.calls.map((number) => ({
                    ...number,
                    isActive: false,
                    isHold: true,
                    isTimerActive: false
                }))
                const nextNumber = {
                    isCalling: false,
                    isActive: true,
                    isTrying: true,
                    number: payload.number,
                    direction: CallDirection.Outgoing,
                    isTimerActive: false,
                    timer: null,
                    isHold: false,
                    isMuteMic: false
                }
                return produce(state, (draft) => {
                    draft.calls = [...deactivatedNumbers, nextNumber]
                })
            }
            return state

        case CALL.CHANGE_TIMER:
            const numbers = state.calls.map((item) => {
                const number = { ...item }
                if (item.number === payload.number) {
                    number.timer = payload.seconds
                }
                return number
            })
            return produce(state, (draft) => {
                draft.calls = numbers
            })

        case CALL.CHANGE_ACTIVE:
            return produce(state, (draft) => {
                draft.calls = state.calls.map((item) => {
                    const newItem = { ...item }
                    newItem.isActive = newItem.number === payload.number
                    return newItem
                })
            })

        case CALL.TRANSFER_SUCCESS:
            return produce(state, (draft) => {
                draft.isTransferSuccess = true
            })

        case CALL.TRANSFER_FAIL:
            return produce(state, (draft) => {
                draft.isTransferFail = true
            })

        case CALL.TRANSFER_CLEAR:
            return produce(state, (draft) => {
                draft.isTransferSuccess = false
                draft.isTransferFail = false
                draft.isOnTransfer = false
            })

        case CALL.SET_TRANSFER:
            return produce(state, (draft) => {
                draft.transferNumber = payload.transferNumber
                draft.isOnTransfer = payload.isOnTransfer
            })

        case CALL.VALIDATION_ERROR_CALL:
            return produce(state, (draft) => {
                draft.validationPhoneError = payload
            })

        case CALL.CLEAR_ALERT:
            return produce(state, (draft) => {
                draft.validationPhoneError = null
            })

        case CALL.CREATE_INTEGRATION_CONTACT:
            return produce(state, (draft) => {
                const callIndex = getCallIndex(state, payload.number)
                draft.calls = state.calls
                draft.calls[callIndex].callerInsights.isCreatingContact = true
            })

        case CALL.SEARCH_INTEGRATION_NUMBER_SUCCESS:
            return produce(state, (draft) => {
                const insightsData = payload?.data
                const callIndex = getCallIndex(state, payload.number)
                draft.calls = state.calls
                if (insightsData) {
                    draft.calls[callIndex] = {
                        ...draft.calls[callIndex],
                        callerInsights: { ...insightsData }
                    }
                }
            })

        case CALL.CREATE_INTEGRATION_CONTACT_SUCCESS:
            return produce(state, (draft) => {
                const insightsData = payload?.contact_data
                const callIndex = getCallIndex(state, payload.number)
                draft.calls = state.calls

                if (insightsData) {
                    draft.calls[callIndex] = {
                        ...draft.calls[callIndex],
                        callerInsights: { ...insightsData, isCreatingContact: false }
                    }
                }
            })

        case CALL.CREATE_INTEGRATION_CONTACT_FAILED:
            return produce(state, (draft) => {
                const callIndex = getCallIndex(state, payload.number)
                draft.calls = state.calls
                draft.calls[callIndex].callerInsights.isCreatingContact = false
            })

        case CALL.SEARCH_INTEGRATION_NUMBER_FAILED:
            return produce(state, (draft) => {
                const callIndex = getCallIndex(state, payload.number)
                draft.calls = state.calls
                draft.calls[callIndex] = {
                    ...draft.calls[callIndex],
                    callerInsights: { records: [] }
                }
            })

        default:
            return state
    }
}

const getCallIndex = (state, number) => {
    return state.calls.findIndex((item) => item.number === number)
}
